import React, { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Canonical modal shell for the FE. Every form/dialog in the app should
 * mount through this component — no more ad-hoc `fixed inset-0` divs.
 *
 * Layout: header (title + close X) / body (scrollable) / footer (actions).
 * Footer is optional; if you render <FormSubmit/> inside <Form/> in the body,
 * leave footer unset and the form's own submit row renders inline.
 *
 * Props:
 *   open       — boolean. Component renders nothing when false.
 *   onClose    — () => void. Fired on backdrop click, ESC, or X button.
 *   title      — string | ReactNode.
 *   size       — 'sm' | 'md' | 'lg' | 'xl' | 'full' (default 'md').
 *   footer     — ReactNode. Optional sticky action row.
 *   closeOnBackdrop — boolean (default true). Set false for destructive flows.
 *   children   — modal body content (typically a <Form/>).
 */
const SIZE_CLASS = {
  sm: "max-w-md",     // ~28rem — confirms, single-field forms
  md: "max-w-lg",     // ~32rem — small forms (5-7 fields)
  lg: "max-w-2xl",    // ~42rem — most forms
  xl: "max-w-4xl",    // ~56rem — wide tables, document previews
  "2xl": "max-w-6xl", // ~72rem — multi-step wizards (create sale, complex flows)
  full: "max-w-[95vw]", // escape hatch — full-bleed only when truly needed
};

const FormModal = ({
  open,
  onClose,
  title,
  size = "md",
  footer,
  closeOnBackdrop = true,
  children,
}) => {
  // ESC to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        // Only close when the mousedown originates on the backdrop itself,
        // not on the modal panel — prevents accidental close while dragging
        // a selection out of an input.
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={`w-full ${SIZE_CLASS[size] || SIZE_CLASS.md} bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden`}>
        {/* Header — dark slate gradient matches the rest of the app's section
            headers (see Onboarding "Onboarding Setup" and Settings sections). */}
        <div className="flex items-start justify-between gap-4 px-6 py-4 bg-gradient-to-r from-slate-800 to-slate-600">
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors -mr-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrolls when content exceeds viewport */}
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {/* Footer (optional) */}
        {footer && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">{footer}</div>
        )}
      </div>
    </div>
  );
};

export default FormModal;
